import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Bell, Mail, Phone, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

export default function NotificationPrefsView() {
  const [prefs, setPrefs] = useState({
    email_notifications: true,
    sms_notifications: false,
    phone_number: '',
    notify_on: ['status_change', 'assignment', 'resolution'],
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/api/notifications/preferences');
        setPrefs(prev => ({ ...prev, ...data }));
      } catch (e) { /* handled */ }
      setLoaded(true);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/notifications/preferences', prefs);
      toast.success('Notification preferences saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const toggleEvent = (evt) => {
    setPrefs(prev => ({
      ...prev,
      notify_on: prev.notify_on.includes(evt)
        ? prev.notify_on.filter(e => e !== evt)
        : [...prev.notify_on, evt],
    }));
  };

  const handleTestEmail = async () => {
    try {
      const { data } = await api.post('/api/notifications/test-email');
      toast.success(`Test email ${data.status}: ${data.email}`);
    } catch { toast.error('Test email failed'); }
  };

  const handleTestSMS = async () => {
    if (!prefs.phone_number) { toast.error('Enter phone number first'); return; }
    try {
      const { data } = await api.post('/api/notifications/test-sms', { phone_number: prefs.phone_number });
      toast.success(`Test SMS ${data.status}: ${data.phone}`);
    } catch { toast.error('Test SMS failed'); }
  };

  if (!loaded) return <div className="flex items-center justify-center py-20"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>;

  const EVENTS = [
    { id: 'status_change', label: 'Ticket status changes', desc: 'When a ticket moves to a new status' },
    { id: 'assignment', label: 'New assignments', desc: 'When you are assigned to a ticket' },
    { id: 'resolution', label: 'Ticket resolution', desc: 'When your ticket is resolved or closed' },
  ];

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl" data-testid="notification-prefs">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">Notification Preferences</h1>
        <p className="text-sm text-gray-500 mt-1">Choose how and when you want to be notified</p>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-5">
        <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Channels</h2>
        <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#D4A843]/10 flex items-center justify-center"><Mail className="w-5 h-5 text-[#D4A843]" strokeWidth={1.5} /></div>
            <div><p className="text-sm font-medium text-gray-900">Email Notifications</p><p className="text-xs text-gray-500">Receive updates to your registered email</p></div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="text-xs rounded" onClick={handleTestEmail} data-testid="test-email-btn">Test</Button>
            <button onClick={() => setPrefs(p => ({ ...p, email_notifications: !p.email_notifications }))} className={`w-11 h-6 rounded-full transition-colors ${prefs.email_notifications ? 'bg-[#D4A843]' : 'bg-gray-300'}`} data-testid="email-toggle">
              <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${prefs.email_notifications ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><Phone className="w-5 h-5 text-emerald-600" strokeWidth={1.5} /></div>
            <div><p className="text-sm font-medium text-gray-900">SMS Notifications</p><p className="text-xs text-gray-500">Text alerts for urgent updates</p></div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="text-xs rounded" onClick={handleTestSMS} data-testid="test-sms-btn">Test</Button>
            <button onClick={() => setPrefs(p => ({ ...p, sms_notifications: !p.sms_notifications }))} className={`w-11 h-6 rounded-full transition-colors ${prefs.sms_notifications ? 'bg-emerald-500' : 'bg-gray-300'}`} data-testid="sms-toggle">
              <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${prefs.sms_notifications ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>
        {prefs.sms_notifications && (
          <div className="animate-fade-in">
            <label className="text-sm font-medium text-gray-700">Phone Number (E.164 format)</label>
            <Input value={prefs.phone_number} onChange={(e) => setPrefs(p => ({ ...p, phone_number: e.target.value }))} placeholder="+1234567890" className="mt-1.5 rounded-lg w-64" data-testid="phone-input" />
          </div>
        )}
        {!prefs.sms_notifications && <input type="hidden" data-testid="phone-input" value="" />}
      </div>
      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-5">
        <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Enterprise Integrations</h2>
        <div className="p-4 rounded-lg bg-gray-50">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#4A154B] flex items-center justify-center"><span className="text-white text-xs font-bold">#</span></div>
            <div><p className="text-sm font-medium text-gray-900">Slack</p><p className="text-xs text-gray-500">Send ticket notifications to a Slack channel</p></div>
          </div>
          <div className="flex items-center gap-2">
            <Input value={prefs.slack_webhook_url || ''} onChange={(e) => setPrefs(p => ({ ...p, slack_webhook_url: e.target.value }))} placeholder="https://hooks.slack.com/services/..." className="rounded-lg flex-1 text-xs" data-testid="slack-webhook-input" />
            <Button size="sm" variant="outline" className="text-xs rounded shrink-0" data-testid="test-slack-btn" onClick={async () => {
              if (!prefs.slack_webhook_url) { toast.error('Enter Slack webhook URL'); return; }
              try { const { data } = await api.post('/api/notifications/test-slack', { webhook_url: prefs.slack_webhook_url }); toast.success(`Slack test: ${data.status}`); } catch { toast.error('Slack test failed'); }
            }}>Test</Button>
          </div>
        </div>
        <div className="p-4 rounded-lg bg-gray-50">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#5B5FC7] flex items-center justify-center"><span className="text-white text-xs font-bold">T</span></div>
            <div><p className="text-sm font-medium text-gray-900">Microsoft Teams</p><p className="text-xs text-gray-500">Post updates to a Teams channel via webhook</p></div>
          </div>
          <div className="flex items-center gap-2">
            <Input value={prefs.teams_webhook_url || ''} onChange={(e) => setPrefs(p => ({ ...p, teams_webhook_url: e.target.value }))} placeholder="https://outlook.office.com/webhook/..." className="rounded-lg flex-1 text-xs" data-testid="teams-webhook-input" />
            <Button size="sm" variant="outline" className="text-xs rounded shrink-0" data-testid="test-teams-btn" onClick={async () => {
              if (!prefs.teams_webhook_url) { toast.error('Enter Teams webhook URL'); return; }
              try { const { data } = await api.post('/api/notifications/test-teams', { webhook_url: prefs.teams_webhook_url }); toast.success(`Teams test: ${data.status}`); } catch { toast.error('Teams test failed'); }
            }}>Test</Button>
          </div>
        </div>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
        <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Notify Me About</h2>
        {EVENTS.map(evt => (
          <button key={evt.id} onClick={() => toggleEvent(evt.id)}
            className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${prefs.notify_on.includes(evt.id) ? 'bg-[#D4A843]/5 border border-[#D4A843]/20' : 'bg-gray-50 border border-transparent'}`}
            data-testid={`event-${evt.id}`}>
            <div><p className="text-sm font-medium text-gray-900">{evt.label}</p><p className="text-xs text-gray-500">{evt.desc}</p></div>
            <div className={`w-5 h-5 rounded flex items-center justify-center ${prefs.notify_on.includes(evt.id) ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'bg-gray-200'}`}>
              {prefs.notify_on.includes(evt.id) && <Bell className="w-3 h-3" />}
            </div>
          </button>
        ))}
      </div>
      <Button onClick={handleSave} disabled={saving} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="save-prefs-btn">
        {saving ? 'Saving...' : 'Save Preferences'}
      </Button>
    </div>
  );
}
